import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AppWindow, Maximize2, Minimize2, X } from 'lucide-react';
import { Button, Icon } from '@ideoz/ui';
import {
  ApiUrls,
  messageHasPrototyperArtifact,
  resolvePrototypeName,
  resolvePrototypeTitle,
} from '@ideoz/core';
import type { Message } from '@ideoz/core';
import { StreamedArtifactSidePanel } from '../../artifacts';
import { PrototyperPanel } from '../../prototyper';
import { PrototyperPublishMenu } from '../../prototyper/components/PrototyperPublishMenu';
import { versionHasNumber } from '../../prototyper/utils/turnVersions';
import { PrototypeVersionMenu } from '../../prototyper/components/PrototypeVersionMenu';
import {
  buildPrototypeVersionMenu,
  type VersionMenuEntry,
} from '../../prototyper/utils/prototypeVersionMenu';
import { useConversation } from '../context/ConversationContext';
import type { useStreamedArtifactPanel } from '../hooks/useStreamedArtifactPanel';
import type { ConversationFindings } from '../hooks/useConversationFindings';

/**
 * The secondary right-hand panel of the conversation workspace (extracted from
 * `ConversationModule` in Phase 2, slice 3): the prototype panel (with its
 * snapshot-restore error banner and the embedded WebContainer workbench) and
 * the streamed-artifact side panel, which share the same resizable slot.
 *
 * Presentational. The prototype/artifact **layout** (open/collapse, the panel
 * ref, `isSecondaryOpen`) lives in `usePrototypePanel`, and the `<ResizablePanel>`
 * wrapper + `prototypePanelRef` stay in the module (the panel must remain a
 * direct child of the resizable group). The build-stream orchestration stays in
 * the module too. This component only renders the two conditional children —
 * everything on `facade.prototyper`/`facade.header` and the workbench-stream
 * values are read from `useConversation()`; module-owned handlers/flags arrive
 * as props.
 */
export interface PrototyperPaneProps {
  isPrototypeOpen: boolean;
  isConversationPanelCollapsed: boolean;
  onClose: () => void;
  onPublish: (slug: string | null, options?: { republish?: boolean }) => void | Promise<void>;
  /** Unpublish one version (the one on screen); the others keep their links. */
  onUnpublishVersion?: (versionId: string) => Promise<void>;
  onToggleConversationPanel: () => void;
  currentPrototypeId: number | null;
  currentPrototypeSlug: string | null;
  currentProjectId: string | null;
  /** The single findings-state instance owned by ConversationModule (design-insights open). */
  findings: ConversationFindings;
  /** The streamed-artifact side-panel state (shares this slot). */
  streamedArtifact: ReturnType<typeof useStreamedArtifactPanel>;
  conversationDirection: 'rtl' | 'ltr' | undefined;
  /** Opens a version the way clicking its card in the chat does (the title menu uses it). */
  onOpenVersion?: (
    prototyperDemoLinkId?: string | number,
    meta?: { llmInteractionId?: string | null; continueBuild?: boolean; didNotFinish?: boolean }
  ) => void;
}

export function PrototyperPane({
  isPrototypeOpen,
  isConversationPanelCollapsed,
  onClose,
  onPublish,
  onUnpublishVersion,
  onToggleConversationPanel,
  currentPrototypeId,
  currentPrototypeSlug,
  currentProjectId,
  findings,
  streamedArtifact,
  conversationDirection,
  onOpenVersion,
}: PrototyperPaneProps): JSX.Element {
  const { t } = useTranslation();
  const { facade, workbench } = useConversation();

  /**
   * The header names what is on screen: the prototype whose version the workbench is showing,
   * titled exactly as its pill is (`resolvePrototypeTitle`). It used to read "Prototype View",
   * which says only which panel you are looking at -- no help at all once a conversation holds
   * more than one prototype.
   *
   * The turn is the active snapshot's, or the one building right now; before either exists
   * (the panel opens ahead of the first restore) it is the newest prototyper turn, which is
   * what that restore will land on. A turn with no name keeps the localised default rather
   * than showing a bare "Prototype - V1".
   */
  const paneTitle = useMemo((): string | undefined => {
    const messages: Message[] = facade.state.messages ?? [];
    // While a build runs, the panel shows that build, so the title names its prototype. It used
    // to name the version on screen before the build, which is another prototype's when the
    // founder edits a different one ("Staff Roster - V2" over the Shift Handover build).
    const liveTurnId =
      workbench.isWorkbenchBuildLive === true
        ? (workbench.streamingWorkbenchLlmInteractionId ?? null)
        : null;
    const turnId =
      liveTurnId ??
      workbench.activeSnapshotInteractionId ??
      workbench.streamingWorkbenchLlmInteractionId ??
      null;
    const onTurn = turnId
      ? messages.find((m) => m.llmInteractionId === turnId)
      : undefined;
    // A live build with no name yet keeps the default title rather than another prototype's.
    const source = liveTurnId
      ? onTurn
      : (onTurn ??
        [...messages]
          .reverse()
          .find((m) => m.role === 'assistant' && messageHasPrototyperArtifact(m)));
    if (!source) return undefined;
    const version =
      source.llmInteractionId && workbench.turnVersions?.status === 'ready'
        ? workbench.turnVersions.get(source.llmInteractionId)
        : undefined;
    const fields = {
      prototypeTitle: source.prototypeTitle,
      prototypeDisplayName: source.prototypeDisplayName,
      // The version's own number (per prototype) once the versions are loaded, as on its pill.
      prototypeVersionIndex: version?.number ?? source.prototypeVersionIndex,
      prototypeKind: source.prototypeKind,
      // No number while the version is building or unfinished, as on its pill. The title used
      // to read "V1" during every build: the number is only given when the build finishes.
      unfinished:
        (workbench.isWorkbenchBuildLive === true &&
          source.llmInteractionId === workbench.streamingWorkbenchLlmInteractionId) ||
        versionHasNumber(version) === false,
    };
    return resolvePrototypeName(fields) ? resolvePrototypeTitle(fields) : undefined;
  }, [
    facade.state.messages,
    workbench.activeSnapshotInteractionId,
    workbench.streamingWorkbenchLlmInteractionId,
    workbench.turnVersions,
    workbench.isWorkbenchBuildLive,
  ]);

  // When the WebContainer workbench is live, the prototyper's header controls are
  // merged INTO the Workbench toolbar (one bar, not two): the title on its left,
  // and fullscreen · Publish · close on its right. `PrototyperPanel` then hides its
  // own header (`hideHeader`). Non-streaming paths keep the panel's own header.
  const isWorkbenchLive = Boolean(workbench.isStreamingPrototype);
  const publishedVersions = facade.prototyper.currentPrototyper.publishedVersions;
  const versionMenuGroups = useMemo(
    () =>
      buildPrototypeVersionMenu({
        messages: facade.state.messages ?? [],
        versions: workbench.turnVersions,
        activeTurnId: workbench.activeSnapshotInteractionId ?? null,
        liveTurnId: workbench.isWorkbenchBuildLive
          ? (workbench.streamingWorkbenchLlmInteractionId ?? null)
          : null,
        publishedVersionIds: new Set((publishedVersions ?? []).map((v) => v.versionId)),
      }),
    [
      facade.state.messages,
      workbench.turnVersions,
      workbench.activeSnapshotInteractionId,
      workbench.isWorkbenchBuildLive,
      workbench.streamingWorkbenchLlmInteractionId,
      publishedVersions,
    ]
  );
  const openVersion = (entry: VersionMenuEntry, continueBuild = false): void => {
    onOpenVersion?.(entry.demoLinkId, {
      llmInteractionId: entry.llmInteractionId,
      didNotFinish: entry.state === 'unfinished',
      ...(continueBuild ? { continueBuild: true } : {}),
    });
  };
  const workbenchTitleSlot = onOpenVersion ? (
    <PrototypeVersionMenu
      title={paneTitle ?? t('prototyper.paneTitle')}
      groups={versionMenuGroups}
      // Opening a version while a build runs is refused anyway (the restore throws).
      disabledReason={
        workbench.isWorkbenchBuildLive
          ? 'Wait for the current build to finish to open another version.'
          : null
      }
      onOpen={(entry) => openVersion(entry)}
      onContinue={(entry) => openVersion(entry, true)}
    />
  ) : (
    <div className="flex min-w-0 items-center gap-2">
      <AppWindow size={16} className="text-primary-500 shrink-0" />
      <h2 className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
        {paneTitle ?? t('prototyper.paneTitle')}
      </h2>
    </div>
  );
  // Collapse/expand is the FIRST button on the right side (its own lead slot,
  // before the view toggle). It's bordered like the other controls; only the
  // close button is borderless.
  const workbenchLeadSlot = (
    <Button
      onClick={onToggleConversationPanel}
      variant="outline"
      size="sm"
      className="!p-1.5 h-8 w-8 shrink-0"
      aria-label={
        isConversationPanelCollapsed
          ? t('prototyper.showConversation')
          : t('prototyper.hideConversation')
      }
    >
      <Icon icon={isConversationPanelCollapsed ? Minimize2 : Maximize2} size={16} />
    </Button>
  );
  // The version on screen, to tell the menu whether the link already serves it.
  const activeVersionId =
    workbench.turnVersions?.status === 'ready' && workbench.activeSnapshotInteractionId
      ? (workbench.turnVersions.get(workbench.activeSnapshotInteractionId)?.id ?? null)
      : null;
  const workbenchActionsSlot = (
    <>
      <PrototyperPublishMenu
        onPublish={onPublish}
        onUnpublishVersion={onUnpublishVersion}
        prototyperDemoLinkId={currentPrototypeId || undefined}
        prototyperSlug={currentPrototypeSlug || undefined}
        publishedVersionId={facade.prototyper.currentPrototyper.publishedVersionId ?? null}
        publishedVersions={facade.prototyper.currentPrototyper.publishedVersions ?? []}
        activeVersionId={activeVersionId}
        userName={facade.header.headerProps.user?.name}
        platformLink={ApiUrls.platformLink}
        publishReady={workbench.streamingPublishReady}
        disabledReason={workbench.streamingPublishDisabledReason}
        previewReady={workbench.streamingPreviewReady}
        isLoading={workbench.streamingWorkbenchLoading}
      />
      <Button
        onClick={onClose}
        variant="ghost"
        size="sm"
        className="!p-1.5 h-8 w-8"
        aria-label={t('prototyper.close')}
      >
        <Icon icon={X} size={16} />
      </Button>
    </>
  );

  return (
    <>
      {isPrototypeOpen && (
        <PrototyperPanel
          isOpen={isPrototypeOpen}
          title={paneTitle}
          hideHeader={isWorkbenchLive}
          onClose={onClose}
          // Only the true cold-start wait (WebContainer booting, snapshot bootstrap pending)
          // owns this illustrated animation — `isWorkbenchBuildLive` used to also gate it,
          // which unmounted/remounted `WorkbenchPanel`/`Workbench` mid-session (losing its
          // local iframe/bridge state) every time it flickered, causing the loading text to
          // jump around. Once cold-start clears, `Workbench`'s own loading-phase ladder
          // (`deriveWorkbenchLoadingPhase`) is the sole, continuous owner of loading text.
          isLoading={workbench.isStreamingPrototype ? workbench.streamingWorkbenchLoading : false}
          error={facade.prototyper.prototypeError}
          onPublish={onPublish}
          prototyperDemoLinkId={currentPrototypeId || undefined}
          prototyperSlug={currentPrototypeSlug || undefined}
          userName={facade.header.headerProps.user?.name}
          platformLink={ApiUrls.platformLink}
          onDesignInsightsClick={currentProjectId ? findings.openDesignInsights : undefined}
          onToggleConversationPanel={onToggleConversationPanel}
          isConversationPanelCollapsed={isConversationPanelCollapsed}
          publishReady={
            workbench.isStreamingPrototype ? workbench.streamingPublishReady : undefined
          }
          publishDisabledReason={
            workbench.isStreamingPrototype ? workbench.streamingPublishDisabledReason : null
          }
          // Publish can't deploy anything on the legacy (non-WebContainer) path — hide it
          // there entirely instead of offering a control that's guaranteed to fail.
          publishUnavailable={!workbench.isStreamingPrototype}
          previewReady={workbench.isStreamingPrototype ? workbench.streamingPreviewReady : true}
        >
          {workbench.isStreamingPrototype ? (
            <>
              {workbench.workbenchSnapshotRestoreRetrying ? (
                <div
                  className="mx-3 mb-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                  role="status"
                >
                  Restoring your prototype…
                </div>
              ) : null}
              {workbench.workbenchSnapshotRestoreNotice ? (
                <div
                  className="mx-3 mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
                  role="status"
                >
                  {workbench.workbenchSnapshotRestoreNotice}
                </div>
              ) : null}
              {workbench.workbenchSnapshotBootstrap === 'failed' &&
              workbench.workbenchSnapshotRestoreError ? (
                <div className="mx-3 mb-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 flex flex-wrap items-center gap-2">
                  <span>
                    Could not restore your last prototype snapshot:{' '}
                    {workbench.workbenchSnapshotRestoreError}. Auto-build is paused until you retry
                    or start a new build.
                  </span>
                  {workbench.onRetrySnapshotBootstrap ? (
                    <button
                      type="button"
                      className="underline font-medium"
                      onClick={workbench.onRetrySnapshotBootstrap}
                    >
                      Retry restore
                    </button>
                  ) : null}
                </div>
              ) : null}
              {workbench.workbenchPersistError ? (
                <div className="mx-3 mb-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 flex flex-wrap items-center gap-2">
                  <span>{workbench.workbenchPersistError}</span>
                  {workbench.onCancelWorkbenchStream ? (
                    <button
                      type="button"
                      className="underline font-medium"
                      onClick={workbench.onCancelWorkbenchStream}
                    >
                      Cancel
                    </button>
                  ) : null}
                </div>
              ) : null}
              {workbench.renderWorkbenchPanel?.({
                isOpen: isPrototypeOpen,
                onClose,
                onToggleConversationPanel,
                isConversationPanelCollapsed,
                embedded: true,
                headerLeftSlot: workbenchTitleSlot,
                headerRightLeadSlot: workbenchLeadSlot,
                headerRightSlot: workbenchActionsSlot,
              })}
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center p-6 text-center text-sm text-gray-500">
              Live prototyping needs a browser that supports cross-origin isolation. Try the
              latest Chrome, Edge, or Firefox to build and publish a prototype here.
            </div>
          )}
        </PrototyperPanel>
      )}
      {streamedArtifact.isOpen && !isPrototypeOpen && (
        <StreamedArtifactSidePanel
          artifact={streamedArtifact.activeArtifact}
          isOpen={streamedArtifact.isOpen}
          onClose={streamedArtifact.close}
          onToggleConversationPanel={onToggleConversationPanel}
          isConversationPanelCollapsed={isConversationPanelCollapsed}
          isRtl={conversationDirection === 'rtl'}
          onSave={streamedArtifact.save}
          onSaveStructured={streamedArtifact.saveStructured}
          onShare={streamedArtifact.share}
        />
      )}
    </>
  );
}

export default PrototyperPane;
